import React, { useEffect, FC } from 'react';
import { isEmpty } from 'lodash';
import { graphql } from 'gatsby';
import { MDXProvider } from '@mdx-js/react';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import MuiAvatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import CardHeader from '@material-ui/core/CardHeader';
import styled from '@emotion/styled';
import { Layout, Video, BlogToolbar, MDXComponents, FaceBookShareAndLike } from '@components';
import { mappingOgUrl } from '@src/util';

const Container = styled.main`
    display: flex;
    flex-direction: row;
    padding: 1em;
    flex-wrap: wrap;
    word-break: word-wrap;
    .article {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex-basis: 60%;
        ${(props) => props.theme.breakpoints.down('sm')} {
            flex-basis: 100%;
        }
    }
`;

const Avatar = styled(MuiAvatar)`
    width: ${(props) => props.theme.spacing(10)}px;
    height: ${(props) => props.theme.spacing(10)}px;
`;
type IProps = {
    data: Typing.Query;
};
const BlogTemplate: FC<IProps> = ({ data }) => {
    const { mdx, site } = data;
    const { frontmatter, tableOfContents, fields, body } = mdx;
    const { siteMetadata } = site;
    const { deploy } = siteMetadata;

    const handleScrollToFix = (): void => {
        window.scrollTo({ top: window.scrollY - 150, behavior: 'smooth' });
    };

    useEffect(() => {
        if (typeof window !== `undefined`) {
            window.addEventListener('hashchange', handleScrollToFix);
            return () => window.removeEventListener('hashchange', handleScrollToFix);
        }
    }, []);

    return (
        <Layout
            title={frontmatter.title}
            ogImage={frontmatter?.bannerPhoto?.childImageSharp?.fluid?.src}
            slug={fields.slug}
        >
            <Container>
                <article className="article">
                    <Typography variant="h4">{frontmatter.title}</Typography>
                    <CardHeader
                        avatar={
                            <Avatar
                                src={frontmatter?.authorPhoto?.childImageSharp?.fluid?.src || null}
                                alt={frontmatter?.authorPhoto?.childImageSharp?.fluid?.originalName || null}
                            />
                        }
                        title={frontmatter.author}
                        subheader={frontmatter.date}
                    />
                    <FaceBookShareAndLike href={mappingOgUrl(deploy.site_url, fields.slug, deploy.path_prefix)} />
                    <hr />
                    {frontmatter?.youtubePath && <Video src={frontmatter.youtubePath} title={frontmatter.title} />}
                    <hr />
                    <MDXProvider components={MDXComponents}>
                        <MDXRenderer>{body}</MDXRenderer>
                    </MDXProvider>
                    <FaceBookShareAndLike href={mappingOgUrl(deploy.site_url, fields.slug, deploy.path_prefix)} />
                </article>
                {!isEmpty(tableOfContents) && <BlogToolbar tableOfContents={tableOfContents} />}
            </Container>
        </Layout>
    );
};
export default BlogTemplate;
export const pageQuery = graphql`
    query($id: String!) {
        mdx(id: { eq: $id }, frontmatter: { type: { eq: "blog" }, published: { eq: true } }) {
            body
            tableOfContents
            frontmatter {
                date(formatString: "YYYY/MM/DD")
                title
                category
                tags
                author
                authorPhoto {
                    childImageSharp {
                        fluid {
                            src
                            originalName
                        }
                    }
                }
                bannerPhoto {
                    childImageSharp {
                        fluid {
                            src
                            originalName
                        }
                    }
                }
                youtubePath
            }
            fields {
                slug
            }
        }
        site {
            siteMetadata {
                deploy {
                    site_url
                    path_prefix
                }
            }
        }
    }
`;
